import React, { useEffect, useState } from 'react';
import apiService from '../../../services/apiService';

const ACTION_ICONS: Record<string, string> = {
  login: '🔑',
  lead_created: '✨',
  lead_assigned: '👤',
  status_change: '🔄',
  note: '📝',
  call: '📞',
  meeting: '🤝',
  follow_up: '⏰',
  customer_created: '🚗',
  workflow_update: '⚙️',
};

const ActivityLogs: React.FC = () => {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterUser, setFilterUser] = useState('');
  const [filterType, setFilterType] = useState('');
  const [users, setUsers] = useState<any[]>([]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const [logsRes, usersRes] = await Promise.all([
        apiService.getAdminActivityLogs(),
        apiService.getCrmUsers()
      ]);
      if (logsRes.success) setLogs(logsRes.data);
      if (usersRes.success) setUsers(usersRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchLogs(); }, []);

  const filteredLogs = logs.filter(l => {
    if (filterUser && l.performed_by?.id !== filterUser) return false;
    if (filterType && l.action_type !== filterType) return false;
    return true;
  });

  const formatTime = (ts: string) => new Date(ts).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });

  const allTypes = Array.from(new Set(logs.map((l: any) => l.action_type)));

  if (loading) {
    return <div className="crm-loading"><div className="crm-spinner"></div> Loading activity logs...</div>;
  }

  return (
    <div>
      <div className="crm-page-header">
        <div className="crm-page-header-left">
          <h2>Activity Audit Log</h2>
          <p>Complete record of all user actions in the CRM</p>
        </div>
        <button className="crm-btn crm-btn-ghost" onClick={fetchLogs}>↻ Refresh</button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <select
          className="crm-status-select"
          value={filterUser}
          onChange={e => setFilterUser(e.target.value)}
          style={{ minWidth: '160px' }}
        >
          <option value="">All Users</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <select
          className="crm-status-select"
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
          style={{ minWidth: '160px' }}
        >
          <option value="">All Action Types</option>
          {allTypes.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>
        {(filterUser || filterType) && (
          <button className="crm-btn crm-btn-ghost" onClick={() => { setFilterUser(''); setFilterType(''); }}>
            ✕ Clear Filters
          </button>
        )}
      </div>

      <div className="crm-table-wrapper">
        <table className="crm-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>User</th>
              <th>Action</th>
              <th>Details</th>
              <th>Entity</th>
            </tr>
          </thead>
          <tbody>
            {filteredLogs.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: '30px', color: 'var(--crm-text-muted)' }}>No logs found.</td></tr>
            ) : filteredLogs.map(log => (
              <tr key={log.id}>
                <td style={{ fontSize: '12px', color: 'var(--crm-text-muted)', whiteSpace: 'nowrap' }}>{formatTime(log.created_at)}</td>
                <td>
                  <div style={{ fontWeight: 600, fontSize: '13px' }}>{log.performed_by?.name || 'System'}</div>
                  {log.performed_by?.role && (
                    <div style={{ fontSize: '11px', color: 'var(--crm-text-muted)' }}>{log.performed_by.role}</div>
                  )}
                </td>
                <td>
                  <span className="crm-status-badge" style={{ background: 'var(--crm-primary-light)', color: 'var(--crm-primary)' }}>
                    {ACTION_ICONS[log.action_type] || '📌'} {log.action_type.replace(/_/g, ' ')}
                  </span>
                </td>
                <td style={{ fontSize: '13px', maxWidth: '280px' }}>{log.description}</td>
                <td style={{ fontSize: '12px', color: 'var(--crm-text-muted)' }}>{log.entity_type || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ActivityLogs;
